import type { AgentTriggerActor, EvalVerdict, HumanFeedbackFilter } from '@liza/db';
import type { RecentTrace } from './types';

// `satisfies Record<Union, …>` keeps each dropdown exhaustive: a value added to the
// underlying union (the status enum, the EvalVerdict type) fails to compile until it
// gets a label here, so the filters can't silently drift out of sync.
const STATUS_LABELS = {
  running: 'running',
  completed: 'completed',
  aborted: 'aborted',
  errored: 'errored',
} satisfies Record<RecentTrace['status'], string>;

const EVAL_LABELS = {
  pass: 'Pass',
  fail: 'Fail',
  none: 'No eval',
} satisfies Record<EvalVerdict, string>;

const HUMAN_FEEDBACK_LABELS = {
  any: 'Rated',
  negative: 'Negative',
  none: 'Not rated',
} satisfies Record<HumanFeedbackFilter, string>;

/** "Clinician" rather than "provider" is the word staff use for themselves; the stored value keeps
 * chat's `provider` vocabulary, so only this label translates between the two. */
const ACTOR_LABELS = {
  patient: 'Patient',
  provider: 'Clinician',
  system: 'System',
} satisfies Record<AgentTriggerActor, string>;

const toOptions = <T extends string>(labels: Record<T, string>): { value: T; label: string }[] =>
  (Object.keys(labels) as T[]).map((value) => ({ value, label: labels[value] }));

export const STATUS_OPTIONS = toOptions(STATUS_LABELS);
export const EVAL_OPTIONS = toOptions(EVAL_LABELS);
export const HUMAN_FEEDBACK_OPTIONS = toOptions(HUMAN_FEEDBACK_LABELS);
export const ACTOR_OPTIONS = toOptions(ACTOR_LABELS);

/** How a run's actor reads as a bare badge in the list; null for a run that recorded none. */
export const actorLabel = (actor: AgentTriggerActor | null): string | null =>
  actor === null ? null : ACTOR_LABELS[actor];

/**
 * The same fact phrased for the trace header, where the chip sits beside the patient's own name and
 * a bare "Patient" would read as a second label for them rather than as who asked.
 */
export const actorAttribution = (actor: AgentTriggerActor | null): string | null => {
  if (actor === null) return null;
  return actor === 'system' ? 'System-triggered' : `Asked by ${ACTOR_LABELS[actor].toLowerCase()}`;
};

// The labels are exhaustive (see above), so their keys double as the accepted values
// for the route's search-param schema.
const filterValues = <T extends string>(labels: Record<T, string>): [T, ...T[]] => Object.keys(labels) as [T, ...T[]];

export const STATUS_FILTER_VALUES = filterValues(STATUS_LABELS);
export const EVAL_FILTER_VALUES = filterValues(EVAL_LABELS);
export const HUMAN_FEEDBACK_FILTER_VALUES = filterValues(HUMAN_FEEDBACK_LABELS);
export const ACTOR_FILTER_VALUES = filterValues(ACTOR_LABELS);
